// Marketplace campaigns — brand-owned and creator-facing.
//
// Named apart from useCampaigns.ts, which is the ADVisor ad-platform hook and
// exports a different CampaignStatus. Same reason the API has campaigns-mkt.ts.

import { gql, useQuery, useMutation } from "@apollo/client";
import type { SocialPlatform } from "./useMarketplace";
import { useActiveBrand } from "./useActiveBrand";

export type CampaignObjective = "AWARENESS" | "UGC_CONTENT" | "SALES" | "LAUNCH" | "EVENT";
export type MarketplaceCampaignStatus =
  | "DRAFT"
  | "OPEN"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CANCELLED";
export type CompensationType = "CASH" | "GIFTING" | "HYBRID";

export type DeliverableFormat =
  | "REEL"
  | "FEED_POST"
  | "STORY"
  | "CAROUSEL"
  | "TIKTOK_VIDEO"
  | "YOUTUBE_SHORT"
  | "YOUTUBE_VIDEO"
  | "UGC_RAW";

export interface DeliverableSpec {
  platform: SocialPlatform;
  format: DeliverableFormat;
  quantity: number;
  dueOffsetDays?: number;
}

export interface Campaign {
  id: string;
  title: string;
  objective: CampaignObjective;
  status: MarketplaceCampaignStatus;
  briefMarkdown: string | null;
  briefStructured: Record<string, unknown> | null;
  compensationType: CompensationType;
  budgetTotal: number | null;
  currency: string;
  rateMin: number | null;
  rateMax: number | null;
  commissionPct: number;
  productDescription: string | null;
  productValue: number | null;
  deliverablesSpec: DeliverableSpec[] | null;
  requirements: Record<string, unknown> | null;
  targetCountries: string[];
  targetNiches: string[];
  minFollowers: number | null;
  targetCreatorCount: number | null;
  applicationDeadline: string | null;
  startDate: string | null;
  endDate: string | null;
  /** Empty means ready to publish — computed by the same function the server validates with. */
  missingForPublish: string[];
  brandProfile?: { id: string; companyName: string; logoUrl: string | null } | null;
  createdAt: string;
}

export interface CampaignInput {
  /** Which brand owns the campaign. Honoured on create only. */
  brandProfileId?: string | null;
  title?: string;
  objective?: CampaignObjective;
  briefMarkdown?: string | null;
  briefStructured?: Record<string, unknown> | null;
  compensationType?: CompensationType;
  budgetTotal?: number | null;
  currency?: string;
  rateMin?: number | null;
  rateMax?: number | null;
  productDescription?: string | null;
  productValue?: number | null;
  deliverablesSpec?: DeliverableSpec[] | null;
  requirements?: Record<string, unknown> | null;
  maxRevisions?: number;
  targetCountries?: string[];
  targetNiches?: string[];
  minFollowers?: number | null;
  targetCreatorCount?: number | null;
  applicationDeadline?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

const CAMPAIGN_FIELDS = gql`
  fragment MarketplaceCampaignFields on MarketplaceCampaign {
    id
    title
    objective
    status
    briefMarkdown
    briefStructured
    compensationType
    budgetTotal
    currency
    rateMin
    rateMax
    commissionPct
    productDescription
    productValue
    deliverablesSpec
    requirements
    targetCountries
    targetNiches
    minFollowers
    targetCreatorCount
    applicationDeadline
    startDate
    endDate
    missingForPublish
    createdAt
  }
`;

const BRAND_SUMMARY = gql`
  fragment BrandSummary on MarketplaceCampaign {
    brandProfile {
      id
      companyName
      logoUrl
    }
  }
`;

const GET_MY_CAMPAIGNS = gql`
  ${CAMPAIGN_FIELDS}
  query GetMyCampaigns($brandProfileId: ID) {
    myCampaigns(brandProfileId: $brandProfileId) {
      ...MarketplaceCampaignFields
    }
  }
`;

const GET_CAMPAIGN = gql`
  ${CAMPAIGN_FIELDS}
  ${BRAND_SUMMARY}
  query GetMarketplaceCampaign($id: ID!) {
    campaign(id: $id) {
      ...MarketplaceCampaignFields
      ...BrandSummary
    }
  }
`;

const GET_OPEN_CAMPAIGNS = gql`
  ${CAMPAIGN_FIELDS}
  ${BRAND_SUMMARY}
  query GetOpenCampaigns($input: OpenCampaignFilterInput) {
    openCampaigns(input: $input) {
      ...MarketplaceCampaignFields
      ...BrandSummary
    }
  }
`;

const CREATE_CAMPAIGN = gql`
  ${CAMPAIGN_FIELDS}
  mutation CreateMarketplaceCampaign($input: CampaignInput!) {
    createCampaign(input: $input) {
      ...MarketplaceCampaignFields
    }
  }
`;

const UPDATE_CAMPAIGN = gql`
  ${CAMPAIGN_FIELDS}
  mutation UpdateMarketplaceCampaign($id: ID!, $input: CampaignInput!) {
    updateCampaign(id: $id, input: $input) {
      ...MarketplaceCampaignFields
    }
  }
`;

const PUBLISH_CAMPAIGN = gql`
  ${CAMPAIGN_FIELDS}
  mutation PublishCampaign($id: ID!) {
    publishCampaign(id: $id) {
      ...MarketplaceCampaignFields
    }
  }
`;

const CANCEL_CAMPAIGN = gql`
  ${CAMPAIGN_FIELDS}
  mutation CancelCampaign($id: ID!) {
    cancelCampaign(id: $id) {
      ...MarketplaceCampaignFields
    }
  }
`;

const DELETE_CAMPAIGN = gql`
  mutation DeleteCampaign($id: ID!) {
    deleteCampaign(id: $id)
  }
`;

/**
 * The caller's campaigns. Scoped to whichever brand the switcher has selected;
 * with none selected this is every brand they manage, which is the agency's
 * cross-client view.
 */
export function useMyCampaigns() {
  const { activeBrandId } = useActiveBrand();

  const { data, loading, error, refetch } = useQuery<{ myCampaigns: Campaign[] }>(
    GET_MY_CAMPAIGNS,
    { variables: { brandProfileId: activeBrandId }, fetchPolicy: "cache-and-network" },
  );

  return { campaigns: data?.myCampaigns ?? [], loading, error, refetch };
}

export function useMarketplaceCampaign(id: string | undefined) {
  const { data, loading, error, refetch } = useQuery<{ campaign: Campaign | null }>(GET_CAMPAIGN, {
    variables: { id },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  return { campaign: data?.campaign ?? null, loading, error, refetch };
}

export function useOpenCampaigns(
  input: {
    countries?: string[];
    niches?: string[];
    compensationTypes?: CompensationType[];
  } = {},
) {
  const { data, loading, error } = useQuery<{ openCampaigns: Campaign[] }>(GET_OPEN_CAMPAIGNS, {
    variables: { input },
    fetchPolicy: "cache-and-network",
  });

  return { campaigns: data?.openCampaigns ?? [], loading, error };
}

/**
 * Publish surfaces the server's `missing` extension rather than a generic
 * failure, so the caller can highlight every incomplete field at once instead
 * of discovering them one round-trip at a time.
 */
export interface PublishFailure {
  message: string;
  missing: string[];
}

export function useCampaignMutations() {
  const { activeBrandId } = useActiveBrand();
  const refetch = { refetchQueries: ["GetMyCampaigns"] };

  const [create, { loading: creating }] = useMutation(CREATE_CAMPAIGN, refetch);
  const [update, { loading: updating }] = useMutation(UPDATE_CAMPAIGN, refetch);
  const [publish, { loading: publishing }] = useMutation(PUBLISH_CAMPAIGN, refetch);
  const [cancel, { loading: cancelling }] = useMutation(CANCEL_CAMPAIGN, refetch);
  const [remove, { loading: deleting }] = useMutation(DELETE_CAMPAIGN, refetch);

  return {
    createCampaign: async (input: CampaignInput) => {
      // The selected brand, unless the caller named one explicitly. Null means
      // "no brand selected", which the server reads as their default.
      const { data } = await create({
        variables: { input: { brandProfileId: activeBrandId, ...input } },
      });
      return data?.createCampaign as Campaign;
    },
    updateCampaign: async (id: string, input: CampaignInput) => {
      const { data } = await update({ variables: { id, input } });
      return data?.updateCampaign as Campaign;
    },
    publishCampaign: async (id: string) => {
      try {
        const { data } = await publish({ variables: { id } });
        return data?.publishCampaign as Campaign;
      } catch (e) {
        const gqlError = (e as { graphQLErrors?: Array<{ message: string; extensions?: { missing?: string[] } }> })
          .graphQLErrors?.[0];
        const failure: PublishFailure = {
          message: gqlError?.message ?? (e as Error).message,
          missing: gqlError?.extensions?.missing ?? [],
        };
        throw failure;
      }
    },
    cancelCampaign: async (id: string) => {
      const { data } = await cancel({ variables: { id } });
      return data?.cancelCampaign as Campaign;
    },
    deleteCampaign: async (id: string) => {
      const { data } = await remove({ variables: { id } });
      return Boolean(data?.deleteCampaign);
    },
    busy: creating || updating || publishing || cancelling || deleting,
  };
}
